import React from "react";
import {
  ActivityIndicator,
  DataTable,
  IconButton,
  TouchableRipple,
  useTheme,
} from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { Text } from "../text/text";
import { I18nManager, StyleSheet, View, Image, Platform } from "react-native";
import { Box } from "../layout/box";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Spacer } from "../spacer/spacer";
import { Stack } from "../layout/stack";
import { TouchableOpacity } from "react-native-gesture-handler";
import { spacing } from "../../theme/spacing";
import { translate } from "../../i18n";

const ITEMS_PER_PAGE = 6;

interface IDataTableParams {
  isLoading: boolean;
  headers: [string, string, string, string?];
  data: [any, any, any, any?, any?];
}

export const CustomDataTable = ({ isLoading, headers = [], data = [] }: IDataTableParams) => {
  const theme = useTheme() as ThemeType;
  const { fontSize, colors, iconSize } = theme;
  const [page, setPage] = React.useState(0);
  const from = page * ITEMS_PER_PAGE;
  const to = (page + 1) * ITEMS_PER_PAGE;
  const styles = StyleSheet.create({
    tableHeader: {
      backgroundColor: theme.colors.darkBg,
      paddingHorizontal: 0,
      height: 30,
    },
    titleContainerStyle: {
      borderLeftColor: theme.colors.white,
      borderWidth: 1,
      marginRight: 2,
      paddingVertical: 0,
      paddingLeft: theme.spacing.smaller,
    },
    dataCellStyle: {
      paddingLeft: theme.spacing.smaller,
    },
    smallCell: {
      flex: 0.6,
    },
    loading: {
      paddingVertical: spacing.large,
    },
    dataText: {
      fontSize: theme.fontSize.medium,
    },
    titleTextStyle: { color: theme.colors.white, fontSize: theme.fontSize.medium },
    fourthCellSize: {},
    text: {
      fontSize: fontSize.large,
      color: colors.darkGrey,
      textAlign: "center",
    },
    underline: {
      textDecorationLine: "underline",
    },
    removeBlueOutline: {
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });

  const slicedData = data.slice(from, to);
  const firstHeaderStyle = headers[3]
    ? styles.titleContainerStyle
    : [styles.titleContainerStyle, styles.smallCell];
  const thirdHeaderStyle = headers[3]
    ? styles.titleContainerStyle
    : [styles.titleContainerStyle, styles.smallCell];
  return isLoading ? (
    <ActivityIndicator style={styles.loading} />
  ) : (
    <DataTable>
      <DataTable.Header style={[styles.tableHeader, styles.removeBlueOutline]}>
        <DataTable.Title style={[firstHeaderStyle, styles.removeBlueOutline]}>
          <Text style={styles.titleTextStyle}>{headers[0]}</Text>
        </DataTable.Title>
        <DataTable.Title style={[styles.titleContainerStyle, styles.removeBlueOutline]}>
          <Text style={styles.titleTextStyle}>{headers[1]}</Text>
        </DataTable.Title>
        <DataTable.Title style={[thirdHeaderStyle, styles.removeBlueOutline]}>
          <Text style={styles.titleTextStyle}>{headers[2]}</Text>
        </DataTable.Title>
        {headers[3] && (
          <DataTable.Title
            style={[
              styles.titleContainerStyle,
              { paddingLeft: 0, marginRight: theme.spacing.medium },
              styles.amountCellSize,
              styles.removeBlueOutline,
            ]}
            numeric>
            <Text style={styles.titleTextStyle}>{headers[3]}</Text>
          </DataTable.Title>
        )}
      </DataTable.Header>
      {data.length ? (
        <>
          {slicedData.map((data: any, index) => {
            const noPaddingStyle = { paddingHorizontal: 0 };
            const rowStyle =
              index % 2
                ? { ...noPaddingStyle, backgroundColor: theme.colors.lightGrey }
                : noPaddingStyle;
            return (
              <DataTableRow
                rowStyle={rowStyle}
                styles={styles}
                theme={theme}
                key={index}
                keyIndex={index}
                data={data}
              />
            );
          })}
          <Spacer size="small" />
          <CustomPagination
            page={page}
            numberOfPages={Math.ceil(data.length / ITEMS_PER_PAGE) + 1}
            onPageChange={(p) => setPage(p)}
          />
        </>
      ) : (
        <DataTable.Row style={styles.removeBlueOutline}>
          <DataTable.Cell style={styles.removeBlueOutline}>Nothing Here Yet!</DataTable.Cell>
        </DataTable.Row>
      )}
    </DataTable>
  );
};

interface DataTableRowProps {
  rowStyle: any;
  keyIndex: number;
  styles: any;
  theme: any;
  data: any;
}

const DataTableRow = ({ rowStyle, keyIndex, styles, theme, data }: DataTableRowProps) => {
  const firstCellStyle = data.col4
    ? styles.dataCellStyle
    : [styles.dataCellStyle, styles.smallCell];
  const thirdCellStyle = data.col4
    ? styles.dataCellStyle
    : [styles.dataCellStyle, styles.smallCell];
  const thirdCellTextStyle = data.col4 ? styles.dataText : [styles.dataText, styles.underline];
  const rowContent = (
    <DataTable.Row key={keyIndex} style={[rowStyle, styles.removeBlueOutline]}>
      <DataTable.Cell style={[firstCellStyle, styles.removeBlueOutline]}>
        <Text selectable style={styles.dataText}>
          {data.col1}
        </Text>
      </DataTable.Cell>
      <DataTable.Cell style={[styles.dataCellStyle, styles.removeBlueOutline]}>
        <Text selectable style={styles.dataText}>
          {data.col2}
        </Text>
      </DataTable.Cell>
      <DataTable.Cell style={[thirdCellStyle, styles.removeBlueOutline]}>
        <Text selectable style={thirdCellTextStyle}>
          {data.col3}
        </Text>
      </DataTable.Cell>
      {data.col4 && (
        <DataTable.Cell style={[styles.dataCellStyle, styles.removeBlueOutline]} numeric>
          <Text style={styles.dataText}>{data.col4}</Text>
        </DataTable.Cell>
      )}
    </DataTable.Row>
  );
  return Boolean(data.onPress) ? (
    <TouchableOpacity onPress={data.onPress} style={styles.removeBlueOutline}>
      {rowContent}
    </TouchableOpacity>
  ) : (
    rowContent
  );
};

interface CustomPaginationProps {
  page: number;
  numberOfPages: number;
  onPageChange: (p: number) => any;
}
const CustomPagination = ({ page, numberOfPages, onPageChange }: CustomPaginationProps) => {
  const theme = useTheme() as ThemeType;
  const boxHeight = 40;
  const styles = StyleSheet.create({
    container: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    pageButtonContainers: {
      display: "flex",
      flex: 0.8,
      marginHorizontal: theme.spacing.smaller,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    removeBlueOutline: {
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });
  const boxStyle = {
    backgroundColor: theme.colors.palette.lightestBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 5,
    width: boxHeight,
    height: boxHeight,
    borderRadius: 8,
    ...styles.removeBlueOutline,
  };
  const buttonBoxStyle = {
    ...boxStyle,
  };

  const ArrowBox = ({ direction }: any) => {
    const isLeft = direction === "LEFT";
    const name = isLeft ? "chevron-left" : "chevron-right";
    const onPress = () => {
      const incrementer = isLeft ? -1 : 1;
      const newPage = page + incrementer;
      if (newPage < 0 || newPage >= numberOfPages - 1) {
        return false;
      }
      onPageChange(newPage);
    };
    return (
      <TouchableRipple style={boxStyle} onPress={onPress}>
        <View pointerEvents="none">
          <IconButton
            accessibilityLabel={
              isLeft
                ? translate("componentStatic.previousButton")
                : translate("componentStatic.nextButton")
            }
            icon={({ size, color }) => {
              return (
                <MaterialCommunityIcons
                  name={name}
                  size={size}
                  color={color}
                  direction={I18nManager.isRTL ? "rtl" : "ltr"}
                />
              );
            }}
            color={theme.colors.text}
          />
        </View>
      </TouchableRipple>
    );
  };
  const numsToShow = [1, 2, 3, 4]
    .map((num) => {
      const nextPage = page + num;
      if (nextPage < numberOfPages) {
        return nextPage;
      }
      return false;
    })
    .filter(Boolean);
  const NumberBox = ({ num, isSelected }: any) => {
    return (
      <TouchableRipple
        onPress={() => {
          onPageChange(num - 1);
        }}
        style={[
          buttonBoxStyle,
          isSelected
            ? {
                backgroundColor: theme.colors.white,
                borderColor: theme.colors.darkBg,
                borderWidth: 2,
              }
            : {},
        ]}>
        <Text style={{ fontSize: theme.fontSize.large }}>{num}</Text>
      </TouchableRipple>
    );
  };
  return (
    <View style={styles.container}>
      <ArrowBox direction="LEFT" />
      {numsToShow.map((num) => (
        <NumberBox num={num} key={num} isSelected={num === page + 1} />
      ))}
      <ArrowBox direction="RIGHT" />
    </View>
  );
};
